import { useEffect, useState } from 'react'
import {
  Timestamp,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore'
import type { User } from 'firebase/auth'
import type { AttachmentDoc, Entry, EventDoc } from '@racedash/shared'
import { db } from '../lib/firebase'

export interface CatalogueEvent extends EventDoc {
  id: string
}

export interface Attachment extends AttachmentDoc {
  /** The event this attaches to, recovered from the document path — a
   *  collection-group query returns documents without telling you where they
   *  live unless you ask the reference. */
  eventId: string
}

/**
 * Events that have not finished yet.
 *
 * Ordered by when they start, because somebody browsing for what to attend is
 * looking forwards. Past events are excluded rather than paged: they cannot be
 * attached to usefully, since capture is decided while a race is running.
 */
export function useUpcomingEvents(max = 60) {
  const [events, setEvents] = useState<CatalogueEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return onSnapshot(
      query(
        collection(db, 'events'),
        where('endsAt', '>=', Timestamp.now()),
        orderBy('endsAt'),
        limit(max),
      ),
      (snap) => setEvents(snap.docs.map((d) => ({ id: d.id, ...(d.data() as EventDoc) }))),
      (e) => setError(e.message),
    )
  }, [max])

  return { events, error }
}

/**
 * Which events this organisation has attached to.
 *
 * One collection-group query rather than a read per event in the catalogue, and
 * a collection-group query needs two things declared that a plain one does not.
 * This query has now been broken by each of them in turn:
 *
 *  - **The rule must key on a field the query filters.** It keyed on the {orgId}
 *    path segment, which a collection-group query cannot constrain, so Firestore
 *    could not prove the query safe and refused all of it.
 *  - **The index must be declared at collection-group scope.** Single-field
 *    indexes are automatic at collection scope only. Nothing in the test suite
 *    catches this, because the emulator does not enforce indexes at all — it
 *    fails in production and nowhere else.
 *
 * See the attachments fieldOverride in firebase/firestore.indexes.json.
 */
export function useAttachments(orgId: string | undefined) {
  const [attachments, setAttachments] = useState<Attachment[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orgId) {
      setAttachments(null)
      return
    }
    return onSnapshot(
      query(collectionGroup(db, 'attachments'), where('orgId', '==', orgId)),
      (snap) =>
        setAttachments(
          snap.docs.map((d) => ({
            eventId: d.ref.parent.parent?.id ?? '',
            ...(d.data() as AttachmentDoc),
          })),
        ),
      (e) => setError(e.message),
    )
  }, [orgId])

  return { attachments, error }
}

export interface AttachInput {
  captureFrom?: string | null
  captureUntil?: string | null
  continuous?: boolean
  note?: string | null
  /** What the organisation is running here. Empty is valid — attending to watch
   *  is a real thing, and capture does not depend on having entered a car. */
  entries?: Entry[]
}

/**
 * Attach an organisation to an event, which is what causes it to be captured.
 *
 * The document id is the organisation id, so attaching twice is the same as
 * attaching once — there is no way to end up with two rows meaning the same
 * thing, and the reconciler can ask "does anybody want this" without deduping.
 */
export async function attachToEvent(
  eventId: string,
  orgId: string,
  orgName: string,
  user: User,
  input: AttachInput = {},
) {
  return setDoc(doc(db, 'events', eventId, 'attachments', orgId), {
    // Duplicated from the path so a collection-group query can filter on it;
    // the security rule still keys on the path, not on this.
    orgId,
    orgName,
    attachedAt: serverTimestamp(),
    attachedBy: user.uid,
    captureFrom: input.captureFrom ?? null,
    captureUntil: input.captureUntil ?? null,
    continuous: input.continuous ?? false,
    note: input.note ?? null,
    entries: input.entries ?? [],
  })
}

/** Update what an already-attached organisation asked to be captured. */
export async function updateAttachment(eventId: string, orgId: string, input: AttachInput) {
  return setDoc(
    doc(db, 'events', eventId, 'attachments', orgId),
    {
      captureFrom: input.captureFrom ?? null,
      captureUntil: input.captureUntil ?? null,
      continuous: input.continuous ?? false,
      note: input.note ?? null,
      entries: input.entries ?? [],
    },
    { merge: true },
  )
}

/**
 * Record which of the stream's own series names belong to this team's entries.
 *
 * Written on its own rather than through updateAttachment, because that one
 * rewrites the capture window from its input and this is called from the
 * dashboard, which has no business touching what gets captured.
 */
export async function saveEntries(eventId: string, orgId: string, entries: Entry[]) {
  return setDoc(doc(db, 'events', eventId, 'attachments', orgId), { entries }, { merge: true })
}

/**
 * Stop attending.
 *
 * A capture already running is left to finish: killing one mid-session
 * destroys something that cannot be recovered, and letting it run to the end of
 * the day costs pennies. Only the windows that have not started are dropped.
 */
export async function detachFromEvent(eventId: string, orgId: string) {
  return deleteDoc(doc(db, 'events', eventId, 'attachments', orgId))
}
